/**
 * Customer-side executor: applies mouse / keyboard / scroll events received
 * from the agent over the WebRTC data channel by dispatching synthetic DOM
 * events into the kapp page. This is the riskiest module of the co-browsing
 * feature, so the rules below are deliberately conservative.
 *
 * Hard rules:
 *   - A region marked `data-cs-no-remote-control` (or any ancestor) is
 *     completely off-limits — clicks and key events targeting it are dropped.
 *   - Password / credit-card fields never receive synthetic input, even when
 *     the agent has been granted control.
 *   - When `isActive()` returns false the executor short-circuits — no events
 *     leak through after revoke.
 *   - The "remote cursor" overlay is updated on every mousemove so the
 *     customer can always see where the agent is pointing.
 *
 * Coordinates arriving from the agent are normalized to (0..1) inside the
 * customer's *viewport*, so we multiply by `innerWidth`/`innerHeight` here.
 * Scroll position is irrelevant — the agent sees whatever the customer is
 * currently looking at, and clicks land on whatever's visible.
 */

const SENSITIVE_SELECTOR = [
  'input[type="password"]',
  'input[autocomplete*="cc-"]',
  'input[autocomplete*="cc_"]',
  'input[autocomplete*="card"]',
  'input[name*="password" i]',
  'input[name*="card" i]',
  'input[name*="cvv" i]',
  '[data-cs-sensitive]',
].join(',');

function isSensitiveField(el) {
  if (!el) return false;
  if (typeof el.matches === "function" && el.matches(SENSITIVE_SELECTOR)) return true;
  return false;
}

function isOptedOut(el) {
  if (!el) return false;
  let node = el;
  while (node && node !== document.body) {
    if (
      typeof node.hasAttribute === "function" &&
      node.hasAttribute("data-cs-no-remote-control")
    ) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

// Walk into shadow roots so elementFromPoint hits the right target inside
// custom elements / web components.
function deepElementFromPoint(x, y) {
  let el = document.elementFromPoint(x, y);
  while (el && el.shadowRoot) {
    const inner = el.shadowRoot.elementFromPoint(x, y);
    if (!inner || inner === el) break;
    el = inner;
  }
  return el;
}

// Native value setter for <input>/<textarea>. Required so React picks up the
// change — assigning `el.value = …` directly is silently ignored by React's
// synthetic event system.
function setNativeValue(el, value) {
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
}

export function createRemoteControlExecutor({ isActive, setRemoteCursor }) {
  function applyMouseMove(payload) {
    if (!isActive()) return;
    const x = clamp01(payload.x);
    const y = clamp01(payload.y);
    setRemoteCursor({ x, y });
  }

  function applyClick(payload) {
    if (!isActive()) return;
    const px = clamp01(payload.x) * window.innerWidth;
    const py = clamp01(payload.y) * window.innerHeight;
    const el = deepElementFromPoint(px, py);
    if (!el) return;
    if (isOptedOut(el)) return;
    if (isSensitiveField(el)) return;

    // Bring the focus so the next keystroke lands here. This also makes
    // synthetic clicks behave more like real ones for buttons that toggle
    // on focus (e.g. menus that close on blur).
    if (typeof el.focus === "function") {
      try { el.focus({ preventScroll: true }); } catch { el.focus(); }
    }
    el.dispatchEvent(new MouseEvent("mousedown", {
      bubbles: true, cancelable: true, clientX: px, clientY: py,
      button: payload.button ?? 0,
    }));
    el.dispatchEvent(new MouseEvent("mouseup", {
      bubbles: true, cancelable: true, clientX: px, clientY: py,
      button: payload.button ?? 0,
    }));
    el.dispatchEvent(new MouseEvent("click", {
      bubbles: true, cancelable: true, clientX: px, clientY: py,
      button: payload.button ?? 0,
    }));
  }

  function applyKey(payload) {
    if (!isActive()) return;
    const target = document.activeElement;
    if (!target || target === document.body) return;
    if (isOptedOut(target)) return;
    if (isSensitiveField(target)) return;

    const key = payload.key || "";
    const isInputLike =
      target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

    if (payload.type === "keydown" && isInputLike && key.length === 1) {
      // Printable character — splice into the value at the caret so React
      // controlled inputs see it as a real keystroke.
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const before = target.value.slice(0, start);
      const after = target.value.slice(end);
      setNativeValue(target, before + key + after);
      const caret = start + 1;
      try { target.setSelectionRange(caret, caret); } catch { /* ignore */ }
      target.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }

    if (payload.type === "keydown" && key === "Backspace" && isInputLike) {
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      if (start === 0 && end === 0) return;
      const cutStart = start === end ? Math.max(0, start - 1) : start;
      const before = target.value.slice(0, cutStart);
      const after = target.value.slice(end);
      setNativeValue(target, before + after);
      try { target.setSelectionRange(cutStart, cutStart); } catch { /* ignore */ }
      target.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }

    // Everything else — Enter, Tab, arrow keys — dispatch as a generic
    // KeyboardEvent. Won't fire form submit on its own (browsers gate that
    // behind a real user gesture) but most app handlers listen to keydown.
    target.dispatchEvent(new KeyboardEvent(payload.type, {
      key: payload.key,
      code: payload.code,
      bubbles: true,
      cancelable: true,
      shiftKey: !!payload.shift,
      ctrlKey: !!payload.ctrl,
      altKey: !!payload.alt,
      metaKey: !!payload.meta,
    }));
  }

  function applyScroll(payload) {
    if (!isActive()) return;
    const delta = Number(payload.deltaY) || 0;
    if (!delta) return;
    window.scrollBy({ top: clamp(delta, -2000, 2000), behavior: "auto" });
  }

  function apply(event) {
    if (!event || typeof event.type !== "string") return;
    switch (event.type) {
      case "mousemove": return applyMouseMove(event);
      case "click":     return applyClick(event);
      case "keydown":
      case "keyup":     return applyKey(event);
      case "scroll":    return applyScroll(event);
      default: return;
    }
  }

  return { apply };
}

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function clamp01(n) {
  return clamp(Number(n) || 0, 0, 1);
}
